import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faRocket } from '@fortawesome/free-solid-svg-icons';
import Modal from '../ModalConversion';
import Autocomplete from '../Autocomplete';
import BiblioService from '../../services/BiblioService';
import { LANGUAGE_OPTIONS, UNLIMITED, storeInkUrl } from './constants';

const EMPTY = {
    prefix: '',
    titles_limit: UNLIMITED,
    users_limit: UNLIMITED,
    deploy_date: '',
    date_end: '',
    subscription: '',
    url_custom: '',
    lang: 'es',
};

// Deriva el modo/valor de un campo de límite desde el registro (-1 = ilimitado).
function limitFromValue(value) {
    const n = Number(value);
    if (value === '' || value === null || value === undefined) {
        return { mode: 'unlimited', amount: '' };
    }
    if (n < 0) {
        return { mode: 'unlimited', amount: '' };
    }
    return { mode: 'custom', amount: String(n) };
}

function toForm(store) {
    if (!store) return { ...EMPTY };
    const titles = store.titles_limit;
    const users = store.users_limit;
    return {
        prefix: store.prefix || '',
        titles_limit: (titles === null || titles === undefined || titles === '') ? UNLIMITED : Number(titles),
        users_limit: (users === null || users === undefined || users === '') ? UNLIMITED : Number(users),
        deploy_date: store.deploy_date || '',
        date_end: store.date_end || '',
        subscription: store.subscription || '',
        url_custom: store.url_custom || '',
        lang: store.lang || 'es',
    };
}

// Editor de límite: Ilimitados (-1) o Por cantidad (n >= 0).
function LimitField({ label, value, onChange }) {
    const { mode, amount } = limitFromValue(value);

    const setMode = (nextMode) => {
        if (nextMode === 'unlimited') {
            onChange(UNLIMITED);
        } else {
            onChange(amount === '' ? 0 : Number(amount));
        }
    };

    return (
        <div className="col-md-4">
            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>{label}</label>
            <div className="d-flex gap-2">
                <select
                    className="form-select bg-dark text-white border-secondary py-2"
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                >
                    <option value="unlimited">Ilimitados</option>
                    <option value="custom">Por cantidad</option>
                </select>
                {mode === 'custom' && (
                    <input
                        type="number"
                        min="0"
                        className="form-control bg-dark text-white border-secondary py-2"
                        value={amount}
                        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
                    />
                )}
            </div>
        </div>
    );
}

export function BiblioModal({ isOpen, isNew, store, saving, onClose, onSave }) {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState('');
    const [accounts, setAccounts] = useState([]);
    const [loadingAccounts, setLoadingAccounts] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setForm(isNew ? { ...EMPTY } : toForm(store));
            setError('');
        }
    }, [isOpen, isNew, store]);

    // Solo al registrar: cuentas que aún no tienen tienda (buscador de cuentas).
    useEffect(() => {
        if (!isOpen || !isNew) return undefined;
        let alive = true;
        setLoadingAccounts(true);
        BiblioService.listAvailableAccounts()
            .then((data) => { if (alive) setAccounts(Array.isArray(data) ? data : []); })
            .catch((e) => { if (alive) setError(e.message); })
            .finally(() => { if (alive) setLoadingAccounts(false); });
        return () => { alive = false; };
    }, [isOpen, isNew]);

    const selectedAccount = useMemo(
        () => accounts.find((a) => a.value === form.prefix) || null,
        [accounts, form.prefix]
    );

    const set = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    // ¿Se puede desplegar? Las recién creadas sí; las ya desplegadas (status 1) no.
    const isDeployed = !isNew && store && Number(store.biblio_status_deploy) === 1;
    const canDeploy = !isDeployed;

    const submit = async (deploy) => {
        setError('');

        const prefix = (form.prefix || '').trim().toUpperCase();
        if (!prefix) {
            setError('La cuenta es obligatoria');
            return;
        }

        const titlesLimit = Number(form.titles_limit);
        const usersLimit = Number(form.users_limit);

        const payload = {
            prefix,
            titles_limit: titlesLimit,
            users_limit: usersLimit,
            deploy_date: form.deploy_date,
            date_end: form.date_end,
            lang: form.lang,
            deploy,
        };

        if (isNew) {
            // El aprovisionamiento externo espera los mismos límites.
            payload.publications = titlesLimit;
            payload.users = usersLimit;
        } else {
            payload.subscription = form.subscription;
            payload.url_custom = form.url_custom;
        }

        const res = await onSave(payload);
        if (res && res.ok === false) {
            setError(res.msg || 'No se pudo guardar la tienda');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <div className="p-4">
                <h4 className="text-white mb-3">{isNew ? 'Registrar tienda Biblio' : 'Editar tienda Biblio'}</h4>

                {error && <div className="alert alert-danger py-2">{error}</div>}

                <form onSubmit={(e) => { e.preventDefault(); submit(false); }}>
                    <div className="row g-3">
                        <div className="col-md-4">
                            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Cuenta *</label>
                            {isNew ? (
                                <Autocomplete
                                    options={accounts}
                                    value={selectedAccount}
                                    onChange={(option) => set('prefix', option ? option.value : '')}
                                    isDark
                                    isClearable
                                    placeholder={loadingAccounts ? 'Cargando cuentas...' : 'Buscar cuenta...'}
                                />
                            ) : (
                                <input
                                    type="text"
                                    className="form-control bg-dark text-white border-secondary py-2"
                                    value={form.prefix}
                                    disabled
                                />
                            )}
                        </div>

                        <LimitField
                            label="Límite de títulos"
                            value={form.titles_limit}
                            onChange={(v) => set('titles_limit', v)}
                        />
                        <LimitField
                            label="Límite de usuarios"
                            value={form.users_limit}
                            onChange={(v) => set('users_limit', v)}
                        />

                        <div className="col-md-4">
                            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Fecha de alta</label>
                            <input
                                type="date"
                                className="form-control bg-dark text-white border-secondary py-2"
                                value={form.deploy_date}
                                onChange={(e) => set('deploy_date', e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Fin del periodo</label>
                            <input
                                type="date"
                                className="form-control bg-dark text-white border-secondary py-2"
                                value={form.date_end}
                                onChange={(e) => set('date_end', e.target.value)}
                            />
                        </div>
                        <div className="col-md-4">
                            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Idioma</label>
                            <select
                                className="form-select bg-dark text-white border-secondary py-2"
                                value={form.lang}
                                onChange={(e) => set('lang', e.target.value)}
                            >
                                {LANGUAGE_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                            </select>
                        </div>

                        <div className="col-md-4">
                            <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>URL Ink</label>
                            <input
                                type="text"
                                className="form-control bg-dark text-white-50 border-secondary py-2"
                                value={storeInkUrl(form.prefix)}
                                readOnly
                                disabled
                            />
                        </div>

                        {!isNew && (
                            <>
                                <div className="col-md-4">
                                    <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>Suscripción</label>
                                    <input
                                        type="text"
                                        className="form-control bg-dark text-white border-secondary py-2"
                                        value={form.subscription}
                                        onChange={(e) => set('subscription', e.target.value)}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="fw-bold mb-1 d-block text-white-50" style={{ fontSize: '0.85rem' }}>URL propia</label>
                                    <input
                                        type="text"
                                        className="form-control bg-dark text-white border-secondary py-2"
                                        value={form.url_custom}
                                        placeholder="www.midominio.com"
                                        onChange={(e) => set('url_custom', e.target.value)}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <button type="button" className="btn btn-outline-secondary border-secondary text-white-50 px-4" onClick={onClose} disabled={saving}>
                            Cancelar
                        </button>
                        <button type="button" className="btn btn-ink-outline px-4" onClick={() => submit(false)} disabled={saving}>
                            {saving && <FontAwesomeIcon icon={faSync} spin className="me-1" />}
                            Guardar
                        </button>
                        {canDeploy && (
                            <button type="button" className="btn btn-primary px-4" onClick={() => submit(true)} disabled={saving}>
                                {saving && <FontAwesomeIcon icon={faSync} spin className="me-1" />}
                                <FontAwesomeIcon icon={faRocket} className="me-1" />
                                Guardar y Desplegar
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </Modal>
    );
}

export default BiblioModal;
